(function initMoonFrameComposite(root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.IWMoonFrameComposite = api;
}(typeof globalThis !== "undefined" ? globalThis : this, function moonFrameCompositeFactory() {
  const FRAME_IDS = Object.freeze([
    "gold-moon-bead-frame-11mm",
    "platinum-tone-moon-bead-frame-11mm",
    "silver-moon-bead-frame-11mm",
  ]);
  const FRAME_ID_SET = new Set(FRAME_IDS);
  const INSERT_SIZE_MM = 8;
  const INSERT_TOLERANCE_MM = 0.26;
  const EDGE_CLEARANCE_MM = 0.7;
  const ADJACENT_FRAME_CLEARANCE_MM = 0.45;
  const FRAME_ROTATION_OFFSET = Math.PI / 2;
  const CAVITY_BY_SKU = Object.freeze(Object.fromEntries(FRAME_IDS.map((id) => [id, {
    u: 0.67,
    v: 0.50,
  }])));

  function isFrameSku(material) {
    return FRAME_ID_SET.has(String(material && (material.id || material.skuId || material.sku) || ""));
  }

  function isInsertSku(material) {
    const item = material && typeof material === "object" ? material : {};
    const category = String(item.category || item.kind || "").toLowerCase();
    const size = Number(item.sizeMm);
    return item.shape === "round"
      && Number.isFinite(size)
      && Math.abs(size - INSERT_SIZE_MM) <= INSERT_TOLERANCE_MM
      && (category === "crystal" || category === "agate");
  }

  function pair(beads, getSku) {
    const items = Array.isArray(beads) ? beads : [];
    const lookup = typeof getSku === "function" ? getSku : (() => null);
    const pairs = [];
    const usedInsertIds = new Set();
    const strands = new Map();
    items.forEach((bead) => {
      const strand = Number(bead && bead.strand) === 1 ? 1 : 0;
      if (!strands.has(strand)) strands.set(strand, []);
      strands.get(strand).push(bead);
    });

    strands.forEach((strandBeads) => {
      const count = strandBeads.length;
      if (count < 2) return;
      strandBeads.forEach((frame, index) => {
        if (!isFrameSku(lookup(frame.skuId))) return;
        const candidates = [
          strandBeads[(index + 1) % count],
          strandBeads[(index - 1 + count) % count],
        ];
        const insert = candidates.find((candidate) => candidate
          && candidate.id !== frame.id
          && !usedInsertIds.has(candidate.id)
          && isInsertSku(lookup(candidate.skuId)));
        if (!insert) return;
        usedInsertIds.add(insert.id);
        pairs.push({ frameId: frame.id, insertId: insert.id });
      });
    });
    return pairs;
  }

  function cavity(material) {
    const id = String(material && (material.id || material.skuId || material.sku) || "");
    return CAVITY_BY_SKU[id] || { u: 0.67, v: 0.50 };
  }

  function frameAngleForStringAngle(stringAngle) {
    return Number(stringAngle || 0) + FRAME_ROTATION_OFFSET;
  }

  function clearanceForRadius(material, radius, clearanceMm = EDGE_CLEARANCE_MM) {
    const item = material && typeof material === "object" ? material : {};
    const sizeMm = Number(item.sizeMm || item.widthMm || 11);
    if (!(sizeMm > 0) || !(Number(radius) > 0)) return 0;
    return Number(radius) * (Number(clearanceMm || 0) * 2 / sizeMm);
  }

  function frameCenterForInsert(material, {
    insertX = 0,
    insertY = 0,
    angle = 0,
    drawWidth = 0,
    drawHeight = 0,
  } = {}) {
    const seat = cavity(material);
    const localX = (seat.u - 0.5) * Number(drawWidth || 0);
    const localY = (seat.v - 0.5) * Number(drawHeight || 0);
    const cos = Math.cos(Number(angle) || 0);
    const sin = Math.sin(Number(angle) || 0);
    return {
      x: Number(insertX || 0) - (cos * localX - sin * localY),
      y: Number(insertY || 0) - (sin * localX + cos * localY),
    };
  }

  function footprint(material, {
    insertHalfExtent = 0,
    frameHalfExtent = 0,
    drawWidth = 0,
    drawHeight = drawWidth,
    clearance = 0,
    rotationOffset = FRAME_ROTATION_OFFSET,
  } = {}) {
    const seat = cavity(material);
    const localX = (seat.u - 0.5) * Number(drawWidth || 0);
    const localY = (seat.v - 0.5) * Number(drawHeight || 0);
    const cos = Math.cos(Number(rotationOffset || 0));
    const sin = Math.sin(Number(rotationOffset || 0));
    const cavityTangentOffset = localX * cos - localY * sin;
    const cavityNormalOffset = localX * sin + localY * cos;
    const insertHalf = Number(insertHalfExtent || 0);
    const frameHalf = Number(frameHalfExtent || 0);
    const safeClearance = Number(clearance || 0);
    const previousExtent = Math.max(insertHalf, frameHalf + cavityTangentOffset) + safeClearance;
    const nextExtent = Math.max(insertHalf, frameHalf - cavityTangentOffset) + safeClearance;
    return {
      previousExtent,
      nextExtent,
      halfExtent: (previousExtent + nextExtent) / 2,
      centerShift: (previousExtent - nextExtent) / 2,
      totalExtent: previousExtent + nextExtent,
      cavityOffset: Math.hypot(localX, localY),
      cavityTangentOffset,
      cavityNormalOffset,
      rotationOffset,
    };
  }

  return {
    FRAME_IDS,
    INSERT_SIZE_MM,
    INSERT_TOLERANCE_MM,
    EDGE_CLEARANCE_MM,
    ADJACENT_FRAME_CLEARANCE_MM,
    FRAME_ROTATION_OFFSET,
    isFrameSku,
    isInsertSku,
    pair,
    cavity,
    frameAngleForStringAngle,
    clearanceForRadius,
    frameCenterForInsert,
    footprint,
  };
}));
